import { useSessionStorage, type RemovableRef } from '@vueuse/core';
import { cloneDeep } from 'lodash';
import { defineStore } from 'pinia';

import type { AllowableProductModel } from '@/models/allowable-product-model.model';
import type { MatrixProduct } from '@/models/matrix-product.model';
import type { MatrixOrder } from '@/types/matrix.interface';
import type { MatrixOrderProduct } from '@/types/product.interface';
import { isDateAfter, toISOString, toPlainDate } from '../utils/temporalUtils';

interface State {
  products: Record<string, MatrixProduct>;
  activeMatrixOrderIndex: number;
  matrixUpdated: RemovableRef<boolean>;
  matrixOrders: RemovableRef<MatrixOrder[]>;
  selectedProductModelsNames: RemovableRef<Set<string>>;
  selectedItemNumbers: RemovableRef<Set<string>>;
  productModels: RemovableRef<Map<string, AllowableProductModel>>;
}

export const useProductMatrixStore = defineStore('product-matrix', {
  state: (): State => ({
    products: {},
    activeMatrixOrderIndex: 0,
    matrixUpdated: useSessionStorage('productMatrix/matrixUpdated', false),
    matrixOrders: useSessionStorage('productMatrix/matrixOrders', []),
    selectedProductModelsNames: useSessionStorage('productMatrix/selectedProductModelsNames', new Set<string>()),
    selectedItemNumbers: useSessionStorage('productMatrix/selectedItemNumbers', new Set<string>()),
    productModels: useSessionStorage<Map<string, AllowableProductModel>>(
      'productMatrix/productModels',
      new Map<string, AllowableProductModel>(),
      {
        serializer: {
          read: (v) => new Map<string, AllowableProductModel>(JSON.parse(v ?? '[]')),
          write: (v) => JSON.stringify([...v.entries()]),
        },
      },
    ),
  }),

  getters: {
    activeMatrixOrder(state): MatrixOrder {
      return state.matrixOrders[state.activeMatrixOrderIndex];
    },
    selectedProducts(): MatrixOrderProduct[] {
      return this.activeMatrixOrder?.matrixProducts ? Object.values(this.activeMatrixOrder.matrixProducts) : [];
    },
  },

  actions: {
    setProducts(products: Record<string, MatrixProduct>) {
      this.products = products;
    },

    setMatrixOrders(matrixOrders: MatrixOrder[]) {
      this.matrixOrders = matrixOrders;
    },

    setMatrixOrder(matrixOrder: MatrixOrder) {
      this.matrixOrders.push(matrixOrder);
    },

    setActiveMatrixOrderIndex(index: number) {
      if (this.matrixOrders[index]) this.activeMatrixOrderIndex = index;
    },

    updateDateIfLaunchDateMoveToLater() {
      for (const matrixOrder of this.matrixOrders) {
        const latestLaunchDate = this.findLatestProductLaunchDate(matrixOrder);
        this.syncOrderDateWithLaunchDate(matrixOrder, latestLaunchDate);
      }
    },

    findLatestProductLaunchDate(matrixOrder: MatrixOrder): string {
      let latestLaunchDate = toPlainDate(matrixOrder.date);

      for (const productId in matrixOrder.matrixProducts) {
        const product = this.products[productId];

        if (product && isDateAfter(product.launchDate, matrixOrder.date)) {
          latestLaunchDate = toPlainDate(product.launchDate);
        }
      }

      return toISOString(latestLaunchDate);
    },

    syncOrderDateWithLaunchDate(matrixOrder: MatrixOrder, latestLaunchDate: string) {
      const isLaunchDateLater = isDateAfter(latestLaunchDate, matrixOrder.date);

      if (isLaunchDateLater) {
        matrixOrder.date = new Date(latestLaunchDate);
        this.setUpdateStatus(true);
      }
    },

    initMatrixOrder(date: Date = new Date()) {
      if (!this.matrixOrders.length) {
        const matrixOrder: MatrixOrder = {
          date: date,
          matrixProducts: {},
          total: 0,
          isCreatedViaExcelUpload: false,
        };

        this.matrixOrders.push(matrixOrder);
        this.setActiveMatrixOrderIndex(this.matrixOrders.length - 1);
      }
    },

    deleteMatrixOrder(index: number) {
      this.matrixOrders.splice(index, 1);

      if (this.activeMatrixOrderIndex >= this.matrixOrders.length) {
        this.setActiveMatrixOrderIndex(this.matrixOrders.length - 1);
      } else if (index <= this.activeMatrixOrderIndex && this.activeMatrixOrderIndex > 0) {
        this.setActiveMatrixOrderIndex(this.activeMatrixOrderIndex - 1);
      }
    },

    cloneMatrixOrder(index: number) {
      const matrixOrder = cloneDeep(this.matrixOrders[index]);

      this.matrixOrders.push({
        date: matrixOrder.date,
        matrixProducts: matrixOrder.matrixProducts,
        total: matrixOrder.total,
        isCreatedViaExcelUpload: matrixOrder.isCreatedViaExcelUpload,
      });

      const newIndex = this.matrixOrders.length - 1;
      this.setActiveMatrixOrderIndex(newIndex);

      this.setUpdateStatus(true);
    },

    updateMatrixOrder(matrixOrder: Partial<MatrixOrder>, index: number = 0) {
      const current = this.matrixOrders[index];

      this.matrixOrders[index] = {
        ...current,
        ...matrixOrder,
      };

      this.setUpdateStatus(true);
    },

    setMatrixOrderTotal(total: number, index: number = 0) {
      const current = this.matrixOrders[index];

      if (!current || current.total === total) return;

      this.matrixOrders[index] = {
        ...current,
        total,
      };
    },

    setUpdateStatus(status: boolean) {
      this.matrixUpdated = status;
    },

    setProductModels(assortments: Map<string, AllowableProductModel>) {
      this.productModels = assortments;
      this.expandItemNumbersFromAssortments();
    },

    setSelectedProductModels(productModelsNames: Set<string>) {
      this.selectedProductModelsNames = productModelsNames;
    },

    updateSelectedProductModel(productModelNames: Set<string>) {
      productModelNames.forEach((name) => this.selectedProductModelsNames.add(name));
    },

    updateSelectedItemNumbers(itemNumbers: Set<string>) {
      itemNumbers.forEach((itemNumber) => this.selectedItemNumbers.add(itemNumber));
    },

    clearSelectedItemNumbers() {
      this.selectedItemNumbers = new Set<string>();
    },

    addSelectedProduct(allowableProduct: AllowableProductModel) {
      this.selectedProductModelsNames.add(allowableProduct.name);
      allowableProduct.itemNumbers?.forEach((itemNumber) => this.selectedItemNumbers.add(itemNumber));
    },

    removeSelectedProduct(allowableProduct: AllowableProductModel) {
      this.selectedProductModelsNames.delete(allowableProduct.name);
      this.removeOrphanedItemNumbers(allowableProduct);
    },

    addAllSelectedProducts(allowableProducts: AllowableProductModel[]) {
      allowableProducts.forEach((allowableProduct) => {
        this.selectedProductModelsNames.add(allowableProduct.name);
        allowableProduct.itemNumbers?.forEach((itemNumber) => this.selectedItemNumbers.add(itemNumber));
      });
    },

    removeSelectedProducts(allowableProducts: AllowableProductModel[]) {
      allowableProducts.forEach((allowableProduct) => {
        this.selectedProductModelsNames.delete(allowableProduct.name);
        this.removeOrphanedItemNumbers(allowableProduct);
      });
    },

    /**
     * When assortment data arrives, expand selectedItemNumbers to include
     * all item numbers from models that overlap with already-selected ones.
     * This ensures drafts/edits (which only store ordered item numbers)
     * get the full set of available colors for their models.
     */
    expandItemNumbersFromAssortments() {
      if (this.selectedItemNumbers.size === 0) return;

      this.productModels.forEach((model) => {
        const hasOverlap = model.itemNumbers?.some((itemNumber) => this.selectedItemNumbers.has(itemNumber));
        if (hasOverlap) {
          model.itemNumbers?.forEach((itemNumber) => this.selectedItemNumbers.add(itemNumber));
          this.selectedProductModelsNames.add(model.name);
        }
      });
    },

    /**
     * Remove item numbers that belong only to the given product model
     * and are not shared with any other still-selected model.
     */
    removeOrphanedItemNumbers(allowableProduct: AllowableProductModel) {
      allowableProduct.itemNumbers?.forEach((itemNumber) => {
        const sharedWithOther = [...this.selectedProductModelsNames].some((name) =>
          this.productModels.get(name)?.itemNumbers?.includes(itemNumber),
        );
        if (!sharedWithOther) this.selectedItemNumbers.delete(itemNumber);
      });
    },

    reset() {
      this.selectedProductModelsNames = new Set<string>();
      this.selectedItemNumbers = new Set<string>();
      this.productModels = new Map<string, AllowableProductModel>();
      this.activeMatrixOrderIndex = 0;
      this.matrixOrders = [];
      this.products = {};
      this.matrixUpdated = false;
    },
  },
});
